import React, { useEffect, useRef, useState, useCallback } from 'react';
import './MusicPlayer.css';

// Playlist of all tracks - filenames must match exactly what's in public/
const TRACKS = [
  '/Galactic Whiskers.mp3',
  '/Nebula Drift.mp3',
  '/Cat Coin Cabal.mp3',
];

const FADE_DURATION = 2000; // 2 seconds fade
const BASE_VOLUME = 0.035; // Lowered for background ambiance

const MusicPlayer = () => {
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0); // Starts with Galactic Whiskers
  const [trackName, setTrackName] = useState('');

  // Extract track name from path
  const getTrackName = (path) => {
    return path.replace('/', '').replace('.mp3', '').replace('.wav', '');
  };

  // Fade out, switch track, fade in
  const fadeToNextTrack = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const nextIndex = (currentTrackIndex + 1) % TRACKS.length;
    const steps = 20;
    const stepDuration = FADE_DURATION / steps;
    const volumeStep = BASE_VOLUME / steps;
    let currentStep = 0;

    // Clear any existing fade
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
    }

    // Fade out
    fadeIntervalRef.current = setInterval(() => {
      currentStep++;
      const newVolume = Math.max(0, BASE_VOLUME - (volumeStep * currentStep));
      audio.volume = newVolume;

      if (currentStep >= steps) {
        clearInterval(fadeIntervalRef.current);
        
        // Switch track
        setCurrentTrackIndex(nextIndex);
        audio.src = TRACKS[nextIndex];
        setTrackName(getTrackName(TRACKS[nextIndex]));
        
        // Start fade in
        audio.volume = 0;
        audio.play().then(() => {
          let fadeInStep = 0;
          fadeIntervalRef.current = setInterval(() => {
            fadeInStep++;
            const newVolume = Math.min(BASE_VOLUME, volumeStep * fadeInStep);
            audio.volume = isMuted ? 0 : newVolume;

            if (fadeInStep >= steps) {
              clearInterval(fadeIntervalRef.current);
              audio.volume = isMuted ? 0 : BASE_VOLUME;
            }
          }, stepDuration);
        }).catch(console.error);
      }
    }, stepDuration);
  }, [currentTrackIndex, isMuted]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      console.log('[MusicPlayer] Initializing audio element');
      audio.volume = BASE_VOLUME;
      audio.src = TRACKS[currentTrackIndex];
      console.log('[MusicPlayer] Set audio src to:', TRACKS[currentTrackIndex]);
      setTrackName(getTrackName(TRACKS[currentTrackIndex]));
      
      // Don't auto-play - browsers block this. User must click play button.
      setIsPlaying(false);

      // Add error handler
      const handleError = (e) => {
        console.error('[MusicPlayer] Audio error:', e);
        console.error('[MusicPlayer] Audio error code:', audio.error?.code);
        console.error('[MusicPlayer] Audio error message:', audio.error?.message);
      };

      // Handle track ending - fade to next
      const handleEnded = () => {
        console.log('[MusicPlayer] Track ended, fading to next');
        fadeToNextTrack();
      };
      
      // Handle successful load
      const handleCanPlay = () => {
        console.log('[MusicPlayer] ✅ Audio can play, readyState:', audio.readyState);
      };

      audio.addEventListener('error', handleError);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('canplay', handleCanPlay);
      
      return () => {
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('canplay', handleCanPlay);
        if (fadeIntervalRef.current) {
          clearInterval(fadeIntervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update ended listener when fadeToNextTrack changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      fadeToNextTrack();
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [fadeToNextTrack]);

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = BASE_VOLUME;
        audioRef.current.muted = false;
        setIsMuted(false);
      } else {
        audioRef.current.muted = true;
        setIsMuted(true);
      }
    }
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) {
      console.error('[MusicPlayer] No audio element');
      return;
    }
    
    console.log('[MusicPlayer] handlePlayPause called, isPlaying:', isPlaying);
    console.log('[MusicPlayer] Audio src:', audio.src);
    console.log('[MusicPlayer] Audio readyState:', audio.readyState);
    
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play()
        .then(() => {
          console.log('[MusicPlayer] ✅ Playback started');
          setIsPlaying(true);
        })
        .catch((err) => {
          console.error('[MusicPlayer] ❌ Playback failed:', err.message);
          console.error('[MusicPlayer] Error name:', err.name);
          // If it's a user interaction issue, the error will be NotAllowedError
          if (err.name === 'NotAllowedError') {
            alert('Please click anywhere on the page first, then try playing again.');
          }
        });
    }
  };

  // Skip to next track with fade
  const skipTrack = () => {
    fadeToNextTrack();
  };

  return (
    <div className="music-player">
      <audio ref={audioRef} />
      
      <span className="track-name" title={trackName}>
        🎵 {trackName}
      </span>
      
      <button
        className="music-skip-btn"
        onClick={skipTrack}
        title="Next track"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
          <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
        </svg>
      </button>
      
      <button
        className="music-toggle-btn"
        onClick={toggleMute}
        title={isMuted ? "Unmute music" : "Mute music"}
      >
        {isMuted ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
        )}
      </button>
      
      <button
        className="music-play-btn"
        onClick={handlePlayPause}
        title={isPlaying ? "Pause music" : "Play music"}
      >
        {isPlaying ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M8 5v14l11-7z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default MusicPlayer;
